import { type ComponentProps, useState } from 'react';
import translations from '../../translations';
import type { AssetAPI } from '../../types/asset';
import GenericModal from '../generic/modal/GenericModal';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import AssetManager from './AssetManager';

interface AssetManagerModalProps extends Omit<ComponentProps<typeof GenericModal>, 'onHide'> {
    onInsertAsset: (asset: string) => void;
    showAssetModal: boolean;
    setShowAssetModal: (show: boolean) => void;
    allowedExtensions: string | string[];
    filterListToAllowedExtensions: boolean;
}

const AssetManagerModal = ({
    onInsertAsset,
    showAssetModal,
    setShowAssetModal,
    allowedExtensions,
    filterListToAllowedExtensions,
    ...otherProps
}: AssetManagerModalProps) => {
    // Name of asset that the user has clicked on
    const [selectedAssetURL, setSelectedAssetURL] = useState('');
    // Name of asset that has just been deleted
    const [deletedAssetURL, setDeletedAssetURL] = useState('');

    const dispatchNotification = useNotificationDispatch();

    const closeModal = () => {
        setSelectedAssetURL('');
        setDeletedAssetURL('');
        setShowAssetModal(false);
    };

    const insertAsset = () => {
        if (!selectedAssetURL) {
            dispatchNotification({
                type: 'addNotification',
                payload: {
                    type: 'info',
                    message: translations.ASSET_manager_modal_no_asset_selected_message,
                    isPersistent: false,
                },
            });
        } else if (deletedAssetURL === selectedAssetURL) {
            dispatchNotification({
                type: 'addNotification',
                payload: {
                    type: 'info',
                    message: translations.ASSET_manager_modal_asset_deleted_message,
                    isPersistent: false,
                },
            });
        } else {
            try {
                onInsertAsset(selectedAssetURL);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            } finally {
                closeModal();
            }
        }
    };

    const markCurrentAssetAsDeleted = () => {
        setDeletedAssetURL(selectedAssetURL);
    };

    const extractAsset = (asset: AssetAPI) => {
        // So we can keep track of asset currently clicked on
        setSelectedAssetURL(asset.publicUrl);
    };

    return (
        <GenericModal
            backdropClassName="modal-above-TinyMCE"
            title={translations.ASSET_manager_modal_title}
            onHide={closeModal}
            show={showAssetModal}
            renderBody={() => (
                <AssetManager
                    onAssetSelect={extractAsset}
                    onAssetDelete={markCurrentAssetAsDeleted}
                    allowedExtensions={allowedExtensions}
                    filterListToAllowedExtensions={filterListToAllowedExtensions}
                />
            )}
            renderFooter={() => (
                <>
                    <button type="button" onClick={closeModal} className="btn btn-default">
                        {translations.ASSET_manager_modal_close_button_label}
                    </button>
                    <button type="button" onClick={insertAsset} className="btn btn-primary">
                        {translations.ASSET_manager_modal_insert_asset_button_label}
                    </button>
                </>
            )}
            {...otherProps}
        />
    );
};

export default AssetManagerModal;
